import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, styled } from '@mui/material';
import { LinkOff } from '@mui/icons-material';
import { BankAccountOperationStatus } from 'further-types/lp/bank-reconciliation';
import {
  BankAccountOperationSourceLabels,
  ActionRefLabels,
} from 'further-ui/labels';
import {
  CalendarDay,
  DateRange,
  numberToCurrencyString,
} from 'further-ui/utils';
import { useFilters, usePagination } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import SearchField from 'further-ui/components/SearchField';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import getActionLink from 'helpers/lp/getActionLink';
import { SortOrder } from 'further-types/utils';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { MuiDateRangePicker } from 'components/FormElements/DateRangePicker';
import { Column } from 'further-ui/components/Layout';

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.secondary.main,
  textDecoration: 'none',
}));

const ExpandedContentWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(
    0,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
    theme.spacingSizes.sm,
  ),
}));

type Props = {
  bankAccount: {
    id: string;
    vehicleId: string;
  };
};

type Row = {
  id: string;
};

const ReconciledTransactions: React.FC<Props> = ({ bankAccount }) => {
  const pageId = `reconciled-transactions-${bankAccount.id}`;
  const [rowToUnreconcile, setRowToUnreconcile] = useState<Row | null>(null);
  const [canUnreconcile] = useHasPermissions([
    RolePermission.LpEditBankAccountOperation,
  ]);
  const { expandedRows, toggleRowExpansion } = useExpandedRows();

  const pagination = usePagination({
    id: pageId,
    orderBy: 'date',
    order: SortOrder.Desc,
  });

  const { filters, handleChangeFilters } = useFilters<{
    search: string;
    dateRange?: DateRange;
  }>(pageId, {
    defaultFilters: { search: '', dateRange: undefined },
    onFiltersChange: pagination.toFirstPage,
  });

  const [data, listBankAccountOperationsQuery] =
    api.bankAccount.listBankAccountOperations.useSuspenseQuery({
      vehicleId: bankAccount.vehicleId,
      bankAccountId: bankAccount.id,
      status: BankAccountOperationStatus.Reconciled,
      page: pagination.page,
      rowsPerPage: pagination.rowsPerPage,
      orderBy: pagination.orderBy,
      order: pagination.order,
      search: filters.search || undefined,
      dateFrom: filters.dateRange?.startDate
        ? new CalendarDay(filters.dateRange.startDate)
        : undefined,
      dateTo: filters.dateRange?.endDate
        ? new CalendarDay(filters.dateRange.endDate)
        : undefined,
    });

  const operationIds = data.results.map((r) => r.id);

  const [actionsByOperationIdArray, listBankAccountOperationActionsQuery] =
    api.bankAccount.listBankAccountOperationActions.useSuspenseQuery({
      vehicleId: bankAccount.vehicleId,
      bankAccountId: bankAccount.id,
      operationIds,
    });

  const unreconcile = api.bankAccount.unreconcileOperation.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Transaction unreconciled successfully.',
      refetchType: 'none',
      onSuccess: () => {
        setRowToUnreconcile(null);
        listBankAccountOperationsQuery.refetch();
        listBankAccountOperationActionsQuery.refetch();
      },
    }),
  );

  const actionsByOperationId = new Map(
    actionsByOperationIdArray.map(({ operationId, actions }) => [
      operationId,
      actions,
    ]),
  );

  type OperationRow = (typeof data)['results'][number];
  type ActionRow =
    (typeof actionsByOperationIdArray)[number]['actions'][number];

  const actionColumns: Array<TableColumn<ActionRow>> = [
    {
      key: 'ledgerDate',
      label: 'Date',
      width: 120,
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'actionRef',
      label: 'Action',
      width: 150,
      render: (row) => {
        const link = getActionLink(
          row.actionRef,
          row.id,
          row.incomeOrExpenseGroupId,
        );
        const label = ActionRefLabels[row.actionRef];
        return link ? <StyledLink to={link}>{label}</StyledLink> : label;
      },
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      width: 150,
    },
    {
      key: 'description',
      label: 'Description',
      width: 180,
    },
    {
      key: 'unreconciledAmountTcy',
      label: 'Unreconciled amount',
      width: 120,
      render: (row) =>
        numberToCurrencyString(row.unreconciledAmountTcy, {
          fallback: '--',
          currency: row.currencyCode,
          negativeInBrackets: true,
        }),
    },
    {
      key: 'reconciledAmountTcy',
      label: 'Amount reconciled',
      width: 120,
      render: (row) =>
        numberToCurrencyString(row.reconciledAmountTcy, {
          fallback: '--',
          currency: row.currencyCode,
          negativeInBrackets: true,
        }),
    },
  ];

  const columns: Array<TableColumn<OperationRow>> = [
    {
      key: 'date',
      label: 'Date',
      width: 120,
      render: (row) => row.date.toReadable(),
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: 200,
    },
    {
      key: 'description',
      label: 'Description',
      width: 200,
    },
    {
      key: 'spent',
      label: 'Sum',
      width: 200,
      render: (row) =>
        numberToCurrencyString((row.received ?? 0) - (row.spent ?? 0), {
          currency: row.accountCurrency,
          negativeInBrackets: true,
        }),
    },
    {
      key: 'source',
      label: 'Source',
      width: 200,
      render: (row) => BankAccountOperationSourceLabels[row.source],
    },
  ];

  return (
    <Column spacing="lg">
      <PageFilters justify="space-between">
        <Filter size="md">
          <SearchField
            autoSearchOnDebounce
            name="search"
            placeholder="Search by description or counterparty name"
            fullWidth
            value={filters.search}
            onSearch={(search) => handleChangeFilters({ search })}
            allowEmptyQuery
          />
        </Filter>
        <Filter size="md">
          <MuiDateRangePicker
            dateRange={filters.dateRange}
            onDateRangeChange={(dateRange) =>
              handleChangeFilters({ dateRange })
            }
            placeholder="Select date range"
          />
        </Filter>
      </PageFilters>
      <Table
        emptyMessage="No transactions found"
        disableSorting
        columns={columns}
        rows={data.results}
        isLoading={
          listBankAccountOperationsQuery.isFetching ||
          pagination.isPaginationChanging
        }
        pagination={pagination}
        rowsTotalCount={data.totalCount}
        isExpandableRow={(row) =>
          (actionsByOperationId.get(row.id)?.length ?? 0) > 0
        }
        scrollable
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row) => (
          <ExpandedContentWrapper>
            <Table
              scrollable
              columns={actionColumns}
              rows={actionsByOperationId.get(row.id) ?? []}
              disableSorting
            />
          </ExpandedContentWrapper>
        )}
        actions={[
          {
            label: 'Unreconcile',
            icon: LinkOff,
            hidden: !canUnreconcile,
            onClick: setRowToUnreconcile,
          },
        ]}
      />
      {rowToUnreconcile && (
        <ConfirmDialog
          open
          onClose={() => setRowToUnreconcile(null)}
          title="Unreconcile this transaction?"
          content="This will reverse the reconciliation and return the transaction to unreconciled status."
          maxWidth="sm"
          loading={unreconcile.isPending}
          actions={{
            confirm: {
              onClick: () => {
                unreconcile.mutate({
                  vehicleId: bankAccount.vehicleId,
                  bankAccountId: bankAccount.id,
                  bankAccountOperationId: rowToUnreconcile.id,
                });
              },
              label: 'Unreconcile',
              color: 'error',
            },
          }}
        />
      )}
    </Column>
  );
};

export default ReconciledTransactions;
